/**
 * 1stdibs
 * User: James
 * Date: 1/17/14
 */
"use strict";

const SoaModel = require('bunsen/models/SoaModel');

/**
 * @class Category
 * @extends SoaModel
 */
const Category = module.exports = SoaModel.extend();

const _ = require('underscore');
const Categories = require('bunsen/collections/inventory/Categories');
const DimensionSet = require('bunsen/models/inventory/DimensionSet');
const Backbone = require('dibs-backbone');
const verticalHelper = require('dibs-vertical-helpers');
const dataProvider = require('bunsen/helpers/dataProvider');
const endpoints = require('dibs-endpoints');
const getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
const DimensionSets = require('bunsen/collections/inventory/DimensionSets');
const globalCache = require('bunsen/helpers/globalCache');

const deferred = getDeferredMethods().Deferred;
const VERTICAL_CATEGORY_KEYS = {};

VERTICAL_CATEGORY_KEYS[verticalHelper.VERTICAL_FURNITURE] = 'F';
VERTICAL_CATEGORY_KEYS[verticalHelper.VERTICAL_FASHION] = 'V';
VERTICAL_CATEGORY_KEYS[verticalHelper.VERTICAL_JEWELRY] = 'J';
VERTICAL_CATEGORY_KEYS[verticalHelper.VERTICAL_ART] = 'A';

_.extend(Category.prototype, /** @lends Category.prototype */{

    /**
     * Get the correct category endpoint
     * @param {string} categoryKey
     * @returns {string}
     */
    url: function (categoryKey) {
        return endpoints.category(categoryKey || this.get('key'));
    },

    relations: [
        {
            key: 'parent',
            type: Backbone.One,
            relatedModel: Category
        },
        {
            key: 'children',
            type: Backbone.Many,
            collectionType: Categories
        },
        {
            key: 'dimensionSet',
            type: Backbone.One,
            relatedModel: DimensionSet
        }
    ],

    /**
     * @returns {number}
     */
    getDimensionSetId: function () {
        const dimensionSet = this.get('dimensionSet');
        return dimensionSet ? dimensionSet.id : null;
    },

    /**
     * @param {string} key
     * @returns {DimensionSet}
     */
    lookupFullDimensionSetWithKey: function (key) {
        const child = this.findChildByKey(key);
        let dimensionSet;
        if (!child) {
            return null;
        }
        dimensionSet = child.get('dimensionSet');
        return dimensionSet ? this.findFullDimensionSetWithId(dimensionSet.id) : null;
    },

    /**
     * @param {number} dimensionSetId
     * @returns {DimensionSet}
     */
    findFullDimensionSetWithId: function (dimensionSetId) {
        return DimensionSets.allDimensionSets().get(dimensionSetId);
    },

    /**
     * This method has been deprecated because
     * new category versions will have non-unique
     * codes
     * @deprecated
     * @param {string} code
     * @returns {Category}
     */
    findChildByCode: function (code) {
        const children = this.get('children');
        if (children) {
            return children.findByCode(code);
        }
    },

    /**
     * @param {string|number} key
     * @returns {Category}
     */
    findChildByKey: function (key) {
        const children = this.get('children');
        if (children) {
            return children.findByKey(key);
        }
    },

    /**
     * @returns {string}
     */
    getName : function () {
        return this.get('name');
    },

    /**
     * @returns {number}
     */
    getLevel : function () {
        return this.get('level');
    },

    /**
     * @returns {string}
     */
    getBrowseUrl : function () {
        return this.get('browseUrl');
    },

    /**
     * @returns {Category}
     */
    getParent: function () {
        return this.get('parent');
    },

    /**
     * Traverses tree invoking callback on each Category
     * @param {function} callback
     */
    ascendTree : function (callback = () => {}) {
        const parent = this.getParent();

        if (typeof callback === 'function') {
            callback(this);
        }

        if (parent) {
            parent.ascendTree(callback);
        }
    }

});

_.extend(Category, /** @lends Category */{

    _verticalCategoryInstances: {},

    VERTICAL_CATEGORY_KEYS: VERTICAL_CATEGORY_KEYS,

    /**
     * @static
     * @private
     * @param {string} vertical
     * @returns {symbol}
     */
    _getCategoryCacheKeyForVertical: (function () {
        const cacheKeys = {};
        return function (vertical) {
            if (!cacheKeys[vertical]) {
                cacheKeys[vertical] = Symbol('fetching_category_' + vertical);
            }
            return cacheKeys[vertical];
        };
    }()),

    /**
     * @static
     * @public
     * @param {string} vertical
     * @returns {string}
     */
    getCategoryKeyForVertical: function (vertical) {
        return Category.VERTICAL_CATEGORY_KEYS[vertical];
    },
    /**
     * @public
     * @static
     * @returns {Category}
     */
    verticalCategoryInstance : function (vertical) {
        if (!Category._verticalCategoryInstances[vertical]) {
            const category = new Category({ key: Category.getCategoryKeyForVertical(vertical) });
            Category._verticalCategoryInstances[vertical] = category;
        }
        return Category._verticalCategoryInstances[vertical];
    },
    /**
     * @public
     * @static
     * @param {string} vertical
     * @param {object} [fetchOptions] Same options you would pass to SoaCollection.fetch
     * @returns {Deferred.promise}
     */
    fetchVerticalCategoryInstance: function (vertical, fetchOptions) {
        const categoryPromiseKey = Category._getCategoryCacheKeyForVertical(vertical);
        let categoryPromise = globalCache.get(categoryPromiseKey);

        if (!categoryPromise) {
            categoryPromise = Category.verticalCategoryInstance(vertical).fetch(fetchOptions);
            globalCache.set(categoryPromiseKey, categoryPromise);
            categoryPromise.fail(function () {
                globalCache.del(categoryPromiseKey);
            });
        }
        return categoryPromise;
    },

    /**
     * @public
     * @static
     * @memberOf Category
     * @param {string} vertical
     * @param {object} [fetchOptions] Same options you would pass to SoaCollection.fetch
     * @param {string} [dataProviderKey]
     *     Key used by dataProvider to provide data for vertical category instance
     * @returns {Deferred.promise}
     */
    requestVerticalCategoryInstanceUseDP: function (vertical, fetchOptions, dataProviderKey) {
        const data = dataProvider.get(dataProviderKey || (vertical + '_category'));
        let instance;
        if (data) {
            instance = Category.verticalCategoryInstance(vertical);
            instance.clear().set(data);
            return deferred().resolve(data).promise();
        }
        return Category.fetchVerticalCategoryInstance(vertical, fetchOptions);
    }
});



// WEBPACK FOOTER //
// ./~/bunsen/models/inventory/Category.es.js